import { useTranslations } from '~/src/hooks/useTranslations'
import { HTMLAttributes, SyntheticEvent, useEffect } from 'react'
import {
  autocompleteClasses,
  AutocompleteRenderInputParams, Box, BoxProps,
  formControlClasses, FormHelperText, InputLabel,
  TextFieldProps,
  Autocomplete,
  AutocompleteProps
} from '@mui/material'
import useGetPlacePredictions from '~/src/hooks/google-maps/useGetPlacePredictions'
import useGetPlaceDetail from '~/src/hooks/google-maps/useGetPlaceDetail'
import { useEoValue } from '~/src/hooks/useEoState'
import { GOOGLE_MAPS_API_STATE, GoogleMapsApiState } from '~/src/stores/google-maps-api'
import FormikAddressRenderOption from '~/src/components/mui-wrappers/fields/address/FormikAddressRenderOption'
import FormikFieldPropsInterface from '~/src/types/FormikFieldProps'
import FormikAddressRenderInput from '~/src/components/mui-wrappers/fields/address/FormikAddressRenderInput'

interface Props extends BoxProps<'div'> {
  AutocompleteProps?: Pick<AutocompleteProps<string | google.maps.places.AutocompletePrediction | undefined, false, true, true>, 'sx'>,
  TextFieldProps: FormikFieldPropsInterface & Omit<TextFieldProps, 'name' | 'size' | 'InputProps'>,
  noOptionsText?: string,
  onAddressSelected(address: google.maps.GeocoderResult | null): void,
}

const FormikAddressAutocomplete = ({
  noOptionsText,
  onAddressSelected,
  AutocompleteProps,
  TextFieldProps: {
    helperText,
    form,
    name,
    label,
    required,
    ...otherTextFieldProps
  },
  ...boxProps

}: Props) => {
  const { isLoaded } = useEoValue<GoogleMapsApiState>(GOOGLE_MAPS_API_STATE)
  const { t } = useTranslations()
  const { isLoading: isLoadingPlaces, fetch: fetchPlaces, options, setOptions, setValue } = useGetPlacePredictions()
  const { isLoading: isLoadingPlaceDetails, fetch: fetchPlaceDetail } = useGetPlaceDetail()

  const _handleGetOptionLabel = (option: string | google.maps.places.AutocompletePrediction) => {
    return typeof option === 'string' ? option : option.description
  }

  const _handleOnChange = (event: SyntheticEvent, newValue: string | google.maps.places.AutocompletePrediction | null) => {
    if (!newValue) {
      return
    }

    setValue(newValue)

    if (newValue instanceof String) {
      return
    }

    setOptions([ newValue as google.maps.places.AutocompletePrediction, ...options ])
    void fetchPlaceDetail((newValue as google.maps.places.AutocompletePrediction).place_id, onAddressSelected)
  }

  const _handleOnInputChange = (event: SyntheticEvent, newInputValue: string) => {
    if (newInputValue.length < 3) {
      return
    }

    form.values[name] = newInputValue
  }

  const _isOptionEqualToValue = (
    option: string | google.maps.places.AutocompletePrediction | undefined,
    value: string | google.maps.places.AutocompletePrediction | undefined
  ): boolean => {
    if (!option || !value) {
      return false
    }

    if (typeof option === 'string' && typeof value === 'string') {
      return option === value
    }

    if (typeof option === 'string' || typeof value === 'string') {
      return false
    }

    return option.place_id === value.place_id
  }

  useEffect(() => {
    void fetchPlaces(form.values[name])
  }, [ form.values[name] ])

  if (!isLoaded) {
    return null
  }

  return (
    <Box {...boxProps}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <InputLabel
          required={required}
          sx={{ color: 'inherit' }}>
          {label}
        </InputLabel>
        <FormHelperText
          id={`${name}-helper-text`}
          sx={{
            color: 'inherit'
          }}
        >
          {helperText}
        </FormHelperText>
      </Box>
      <Autocomplete
        id='google-places-autocomplete'
        filterSelectedOptions
        filterOptions={(x) => x}
        options={options}
        disableClearable
        value={form.values[name]}
        noOptionsText={noOptionsText ?? t('locator.addressAutocompleteEmpty')}
        isOptionEqualToValue={_isOptionEqualToValue}
        sx={{
          width: '100%',
          [`.${formControlClasses.root}`]: {
            backgroundColor: 'transparent'
          },
          [`.${autocompleteClasses.endAdornment}`]: {
            top: '50%'
          },
          ...AutocompleteProps?.sx ?? {}
        }}
        getOptionLabel={_handleGetOptionLabel}
        onChange={_handleOnChange}
        onInputChange={_handleOnInputChange}
        renderInput={(params: Omit<AutocompleteRenderInputParams, 'id' | 'name' | 'size' | 'inputProps' | 'fullWidth'>) => (
          <FormikAddressRenderInput
            name={name}
            form={form}
            isLoading={isLoadingPlaces || isLoadingPlaceDetails}
            {...params}
            {...otherTextFieldProps}
          />
        )}
        renderOption={({ key, ...props }: HTMLAttributes<HTMLLIElement> & { key: any }, option: string | google.maps.places.AutocompletePrediction) => (
          <FormikAddressRenderOption
            option={option as google.maps.places.AutocompletePrediction}
            key={key}
            {...props}/>
        )}/>
    </Box>
  )
}

export default FormikAddressAutocomplete
